// src/components/PortfolioTables.jsx
// MUI DataGrid tables for Holdings and Positions — Kite-style sortable columns.
// When analysisData is provided, holdings render as a custom expandable table instead.
import * as React from 'react';
import { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';

// ── shared cell renderers ─────────────────────────────────────────────────────

const pnlCell = (params) => {
  const v = Number(params.value || 0);
  return (
    <span style={{ color: v > 0 ? '#16a34a' : v < 0 ? '#dc2626' : '#64748b', fontWeight: 600 }}>
      {v >= 0 ? '+' : ''}₹{Math.abs(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </span>
  );
};

const pctCell = (params) => {
  const v = Number(params.value || 0);
  return (
    <span style={{ color: v > 0 ? '#16a34a' : v < 0 ? '#dc2626' : '#64748b', fontWeight: 600 }}>
      {v >= 0 ? '+' : ''}{v.toFixed(2)}%
    </span>
  );
};

const priceCell = (params) => (
  <span>₹{Number(params.value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
);

// ── Holdings columns ──────────────────────────────────────────────────────────

const holdingsColumns = [
  {
    field: 'tradingsymbol', headerName: 'Symbol', width: 130,
    renderCell: (p) => <strong style={{ color: '#0f172a' }}>{p.value}</strong>,
  },
  { field: 'quantity',      headerName: 'Qty',          type: 'number', width: 80  },
  { field: 'average_price', headerName: 'Avg Price',    type: 'number', width: 120, renderCell: priceCell },
  { field: 'last_price',    headerName: 'LTP',          type: 'number', width: 120, renderCell: priceCell },
  {
    field: 'invested',
    headerName: 'Invested',
    type: 'number', width: 130,
    valueGetter: (_, row) => Number(row.average_price || 0) * Number(row.quantity || 0),
    renderCell: (p) => <span>₹{Number(p.value || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>,
  },
  {
    field: 'current_value',
    headerName: 'Current',
    type: 'number', width: 130,
    valueGetter: (_, row) => Number(row.last_price || 0) * Number(row.quantity || 0),
    renderCell: (p) => <span>₹{Number(p.value || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>,
  },
  { field: 'pnl',                  headerName: 'P&L',       type: 'number', width: 130, renderCell: pnlCell, sortable: true },
  { field: 'day_change',           headerName: 'Day Chg',   type: 'number', width: 110, renderCell: pnlCell, sortable: true },
  { field: 'day_change_percentage',headerName: 'Day %',     type: 'number', width: 90,  renderCell: pctCell, sortable: true },
  {
    field: 'day_change_total',
    headerName: 'Day Chg ₹',
    type: 'number', width: 120, sortable: true,
    valueGetter: (_, row) => Number(row.day_change || 0) * Number(row.quantity || 0),
    renderCell: pnlCell,
  },
];

// ── Positions columns ─────────────────────────────────────────────────────────

const positionsColumns = [
  {
    field: 'tradingsymbol', headerName: 'Symbol', width: 130,
    renderCell: (p) => <strong style={{ color: '#0f172a' }}>{p.value}</strong>,
  },
  {
    field: 'quantity', headerName: 'Qty', type: 'number', width: 80,
    renderCell: (p) => {
      const v = Number(p.value || 0);
      return (
        <span style={{ color: v > 0 ? '#16a34a' : v < 0 ? '#dc2626' : '#64748b', fontWeight: 600 }}>
          {v > 0 ? '+' : ''}{v}
        </span>
      );
    },
  },
  { field: 'product',       headerName: 'Product',   width: 80 },
  { field: 'average_price', headerName: 'Avg Price', type: 'number', width: 120, renderCell: priceCell },
  { field: 'last_price',    headerName: 'LTP',       type: 'number', width: 120, renderCell: priceCell },
  { field: 'pnl',           headerName: 'P&L',       type: 'number', width: 130, renderCell: pnlCell, sortable: true },
  { field: 'unrealised',    headerName: 'Unrealised',type: 'number', width: 130, renderCell: pnlCell, sortable: true },
  { field: 'day_change',    headerName: 'Day Chg',   type: 'number', width: 110, renderCell: pnlCell, sortable: true },
];

// ── Recommendation badge colors ──────────────────────────────────────────────

const REC_COLORS = {
  'ADD MORE': { bg: '#dcfce7', color: '#15803d' },
  'HOLD':     { bg: '#fef9c3', color: '#854d0e' },
  'TRIM':     { bg: '#ffedd5', color: '#9a3412' },
  'EXIT':     { bg: '#fee2e2', color: '#991b1b' },
};

// ── AI Action badge + conviction ─────────────────────────────────────────────

const ACTION_COLORS = {
  ACCUMULATE_NOW:  { bg: '#dcfce7', color: '#15803d' },
  BUY_ON_DIPS:     { bg: '#d1fae5', color: '#065f46' },
  FRESH_BUY:       { bg: '#d1fae5', color: '#065f46' },
  HOLD_STRONG:     { bg: '#fef9c3', color: '#854d0e' },
  HOLD_CAUTIOUS:   { bg: '#ffedd5', color: '#9a3412' },
  REDUCE_ON_RALLY: { bg: '#fee2e2', color: '#991b1b' },
  AVOID_ADDING:    { bg: '#fecaca', color: '#7f1d1d' },
};

function ActionBadge({ action }) {
  const s = ACTION_COLORS[action] || { bg: '#f1f5f9', color: '#475569' };
  return <span style={{ background: s.bg, color: s.color, padding: '0.1rem 0.4rem', borderRadius: 4, fontSize: '0.65rem', fontWeight: 700 }}>{(action || '—').replace(/_/g, ' ')}</span>;
}

function Stars({ n }) {
  return <span style={{ fontSize: '0.6rem', letterSpacing: '1px', color: '#f59e0b' }}>{'★'.repeat(n || 0)}{'☆'.repeat(5 - (n || 0))}</span>;
}

// ── AI Insight row (rendered below AnalysisRow in expanded area) ─────────────

function AiInsightRow({ data, colSpan }) {
  if (!data) return null;
  const pl = data.price_levels || {};
  const fc = data.if_deploying_fresh_capital || {};
  const an = data.analysis || {};
  const fmtP = v => v != null ? '₹' + Number(v).toLocaleString('en-IN') : '—';

  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: 0, border: 'none' }}>
        <div style={{
          background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 6,
          padding: '0.5rem 0.8rem', margin: '0 0.5rem 0.4rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem', flexWrap: 'wrap' }}>
            <ActionBadge action={data.action} />
            <Stars n={data.conviction} />
            {an.thesis_broken && <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#dc2626', background: '#fee2e2', borderRadius: 3, padding: '0 4px' }}>THESIS BROKEN</span>}
          </div>
          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
            {pl.ideal_buy_range_low != null && <span style={{ fontSize: '0.66rem', background: '#e0f2fe', color: '#0369a1', padding: '0.1rem 0.35rem', borderRadius: 3, fontWeight: 600 }}>Buy {fmtP(pl.ideal_buy_range_low)}–{fmtP(pl.ideal_buy_range_high)}</span>}
            {pl.stop_loss != null && <span style={{ fontSize: '0.66rem', background: '#fee2e2', color: '#991b1b', padding: '0.1rem 0.35rem', borderRadius: 3, fontWeight: 600 }}>SL {fmtP(pl.stop_loss)}</span>}
            {pl.target_6m != null && <span style={{ fontSize: '0.66rem', background: '#f3e8ff', color: '#6b21a8', padding: '0.1rem 0.35rem', borderRadius: 3, fontWeight: 600 }}>6m {fmtP(pl.target_6m)}</span>}
            {pl.target_12m != null && <span style={{ fontSize: '0.66rem', background: '#f3e8ff', color: '#6b21a8', padding: '0.1rem 0.35rem', borderRadius: 3, fontWeight: 600 }}>12m {fmtP(pl.target_12m)}</span>}
          </div>
          {fc.suggested_amount > 0 && (
            <div style={{ fontSize: '0.7rem', color: '#7c3aed', fontWeight: 600, marginBottom: '0.25rem' }}>
              Deploy {fmtP(fc.suggested_amount)}{fc.tranche && <span style={{ color: '#64748b', fontWeight: 400 }}> · {fc.tranche.replace(/_/g, ' ')}</span>}
              {fc.deploy_condition && <div style={{ fontSize: '0.66rem', color: '#64748b', fontWeight: 400 }}>{fc.deploy_condition}</div>}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem', fontSize: '0.68rem' }}>
            {an.technical_summary && <div><span style={{ color: '#0369a1', fontWeight: 700, fontSize: '0.6rem', textTransform: 'uppercase' }}>Technical</span><p style={{ color: '#475569', margin: '2px 0 0', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{an.technical_summary}</p></div>}
            {an.fundamental_summary && <div><span style={{ color: '#7c3aed', fontWeight: 700, fontSize: '0.6rem', textTransform: 'uppercase' }}>Fundamental</span><p style={{ color: '#475569', margin: '2px 0 0', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{an.fundamental_summary}</p></div>}
          </div>
        </div>
      </td>
    </tr>
  );
}

// ── Expandable analysis row for the custom table ─────────────────────────────

function AnalysisRow({ data, colSpan }) {
  if (!data) return null;
  const rec = data.recommendation;
  const rs = REC_COLORS[rec] || { bg: '#334155', color: '#e2e8f0' };
  const bd = data.score_breakdown || {};
  const tech = bd.tech || {};
  const f = data.fundamentals;

  const fmt = (v, dec = 1) => v != null ? Number(v).toFixed(dec) : '—';

  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: 0, border: 'none' }}>
        <div style={{
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6,
          padding: '0.55rem 0.8rem', margin: '0 0.5rem 0.4rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
            <span style={{
              background: rs.bg, color: rs.color,
              padding: '0.12rem 0.45rem', borderRadius: 4,
              fontSize: '0.7rem', fontWeight: 700,
            }}>{rec || '—'}</span>
            <span style={{ color: '#0f172a', fontSize: '0.74rem' }}>
              Tech {data.tech_score ?? '—'}/7
            </span>
            <span style={{ color: '#0f172a', fontSize: '0.74rem' }}>
              Fund {data.fund_score ?? '—'}/9
            </span>
            <span style={{ color: '#0369a1', fontSize: '0.74rem', fontWeight: 700 }}>
              Total {data.total_score ?? '—'}/16
            </span>
          </div>
          <div style={{ display: 'flex', gap: '0.85rem', flexWrap: 'wrap', fontSize: '0.7rem', color: '#64748b' }}>
            {data.rsi != null && <span>RSI <strong style={{ color: '#0f172a' }}>{fmt(data.rsi)}</strong></span>}
            {tech.macd_bullish != null && (
              <span>MACD <strong style={{ color: tech.macd_bullish ? '#16a34a' : '#dc2626' }}>
                {tech.macd_bullish ? 'Bull' : 'Bear'}</strong></span>
            )}
            {tech.price_gt_ema50 != null && (
              <span>&gt;EMA50 <strong style={{ color: tech.price_gt_ema50 ? '#16a34a' : '#dc2626' }}>
                {tech.price_gt_ema50 ? 'Yes' : 'No'}</strong></span>
            )}
            {tech.price_gt_ema200 != null && (
              <span>&gt;EMA200 <strong style={{ color: tech.price_gt_ema200 ? '#16a34a' : '#dc2626' }}>
                {tech.price_gt_ema200 ? 'Yes' : 'No'}</strong></span>
            )}
            {tech.volume_breakout && (
              <span style={{ color: '#38bdf8', fontWeight: 600 }}>Vol Breakout</span>
            )}
          </div>
          {f && (
            <div style={{ marginTop: '0.3rem', display: 'flex', gap: '0.85rem', flexWrap: 'wrap', fontSize: '0.7rem', color: '#64748b' }}>
              {f.roe != null && <span>ROE <strong style={{ color: '#0f172a' }}>{fmt(f.roe)}%</strong></span>}
              {f.debt_equity != null && <span>D/E <strong style={{ color: '#0f172a' }}>{fmt(f.debt_equity)}</strong></span>}
              {f.profit_growth != null && <span>Profit G <strong style={{ color: '#0f172a' }}>{fmt(f.profit_growth)}%</strong></span>}
              {f.pe_ratio != null && <span>P/E <strong style={{ color: '#0f172a' }}>{fmt(f.pe_ratio)}</strong></span>}
              {f.promoter_holding != null && <span>Promoter <strong style={{ color: '#0f172a' }}>{fmt(f.promoter_holding)}%</strong></span>}
            </div>
          )}
          {data.analyzed_at && (
            <div style={{ marginTop: '0.25rem', fontSize: '0.63rem', color: '#94a3b8' }}>
              Analyzed: {new Date(data.analyzed_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── Custom sortable table with expandable analysis rows ──────────────────────

const fmtInr = (n, dec = 2) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec });

// Column definitions for the custom table (mirrors holdingsColumns)
const customCols = [
  { key: 'tradingsymbol', label: 'Symbol',    align: 'left',  width: 130 },
  { key: 'quantity',      label: 'Qty',       align: 'right', width: 70  },
  { key: 'average_price', label: 'Avg Price', align: 'right', width: 110 },
  { key: 'last_price',    label: 'LTP',       align: 'right', width: 110 },
  { key: 'invested',      label: 'Invested',  align: 'right', width: 120, getter: r => Number(r.average_price || 0) * Number(r.quantity || 0) },
  { key: 'current_value', label: 'Current',   align: 'right', width: 120, getter: r => Number(r.last_price || 0) * Number(r.quantity || 0) },
  { key: 'pnl',           label: 'P&L',       align: 'right', width: 120 },
  { key: 'day_change_percentage', label: 'Day %', align: 'right', width: 85 },
  { key: 'rec',           label: 'Rating',    align: 'center', width: 95 },
  { key: 'total_score',   label: 'Score',     align: 'center', width: 60 },
];

function ExpandableHoldingsTable({ rows, analysisData, aiData, expandedRows, onToggleExpand, onBuyClick }) {
  const [sortField, setSortField] = useState('pnl');
  const [sortDir, setSortDir]     = useState('desc');

  // Build columns dynamically — add AI columns when aiData present
  const activeCols = React.useMemo(() => {
    const cols = [...customCols];
    if (aiData) {
      cols.push({ key: 'ai_action', label: 'AI Action', align: 'center', width: 115 });
      cols.push({ key: 'ai_tranche', label: 'Tranche', align: 'center', width: 60 });
      cols.push({ key: 'ai_buy_range', label: 'Buy Range', align: 'center', width: 140 });
      cols.push({ key: 'ai_conv',   label: 'Conv.',     align: 'center', width: 55  });
      cols.push({ key: 'ai_add',    label: '',           align: 'center', width: 50  });
    }
    return cols;
  }, [aiData]);

  const getValue = (row, col) => {
    if (col.getter) return col.getter(row);
    const sym = row.tradingsymbol || row.symbol;
    const a = analysisData?.[sym];
    const ai = aiData?.[sym];
    if (col.key === 'rec') return a?.recommendation || '';
    if (col.key === 'total_score') return a?.total_score ?? -1;
    if (col.key === 'ai_action') return ai?.action || '';
    if (col.key === 'ai_conv') return ai?.conviction ?? -1;
    if (col.key === 'ai_tranche') return ai?.if_deploying_fresh_capital?.tranche || '';
    if (col.key === 'ai_buy_range') return ai?.price_levels?.ideal_buy_range_low ?? 0;
    if (col.key === 'ai_add') return 0;
    return Number(row[col.key] || 0);
  };

  const toggleSort = (key) => {
    if (sortField === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(key); setSortDir('desc'); }
  };

  const sorted = [...rows].sort((a, b) => {
    const col = activeCols.find(c => c.key === sortField);
    let va = col ? getValue(a, col) : 0;
    let vb = col ? getValue(b, col) : 0;
    if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb || '').toLowerCase(); }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const thStyle = {
    padding: '0.45rem 0.5rem', fontSize: '0.7rem', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.04em', color: '#64748b',
    background: '#f8fafc', borderBottom: '2px solid #e2e8f0',
    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
  };
  const tdStyle = {
    padding: '0.4rem 0.5rem', fontSize: '0.82rem',
    borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap',
  };

  return (
    <div style={{ width: '100%', overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {activeCols.map(col => (
              <th key={col.key}
                onClick={() => toggleSort(col.key)}
                style={{ ...thStyle, textAlign: col.align, width: col.width }}>
                {col.label}
                {sortField === col.key && (sortDir === 'asc' ? ' ▲' : ' ▼')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const sym = row.tradingsymbol || row.symbol;
            const a = analysisData?.[sym];
            const ai = aiData?.[sym];
            const isExpanded = expandedRows.has(sym);
            const pnl = Number(row.pnl || 0);
            const dayPct = Number(row.day_change_percentage || 0);
            const invested = Number(row.average_price || 0) * Number(row.quantity || 0);
            const current  = Number(row.last_price || 0) * Number(row.quantity || 0);
            const rec = a?.recommendation;
            const recS = REC_COLORS[rec] || {};

            return (
              <React.Fragment key={sym}>
                <tr
                  onClick={() => onToggleExpand(sym)}
                  style={{ cursor: 'pointer', background: isExpanded ? '#f0f9ff' : 'transparent' }}
                  onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                >
                  <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 700, color: '#0f172a' }}>
                    {isExpanded ? '▾ ' : '▸ '}{sym}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{row.quantity}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>₹{fmtInr(row.average_price)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>₹{fmtInr(row.last_price)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>₹{fmtInr(invested, 0)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>₹{fmtInr(current, 0)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: pnl > 0 ? '#16a34a' : pnl < 0 ? '#dc2626' : '#64748b', fontWeight: 600 }}>
                    {pnl >= 0 ? '+' : ''}₹{fmtInr(Math.abs(pnl))}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: dayPct > 0 ? '#16a34a' : dayPct < 0 ? '#dc2626' : '#64748b', fontWeight: 600 }}>
                    {dayPct >= 0 ? '+' : ''}{dayPct.toFixed(2)}%
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {rec ? (
                      <span style={{
                        background: recS.bg || '#334155', color: recS.color || '#e2e8f0',
                        padding: '0.1rem 0.4rem', borderRadius: 4,
                        fontSize: '0.68rem', fontWeight: 700,
                      }}>{rec}</span>
                    ) : '—'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700, color: '#0f172a' }}>
                    {a?.total_score != null ? `${a.total_score}/16` : '—'}
                  </td>
                  {aiData && (
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {ai ? <ActionBadge action={ai.action} /> : '—'}
                    </td>
                  )}
                  {aiData && (() => {
                    const tr = ai?.if_deploying_fresh_capital?.tranche || '';
                    const trLabel = tr === 'WEEK_1_2' ? 'T1' : tr === 'WEEK_3_4' ? 'T2' : tr === 'MONTH_2' ? 'T3' : tr === 'HOLD' ? '—' : tr ? tr.replace(/_/g, ' ') : '—';
                    return <td style={{ ...tdStyle, textAlign: 'center', fontSize: '0.75rem', fontWeight: 700, color: trLabel === 'T1' ? '#16a34a' : trLabel === 'T2' ? '#0369a1' : trLabel === 'T3' ? '#7c3aed' : '#94a3b8' }}>{trLabel}</td>;
                  })()}
                  {aiData && (() => {
                    const pl = ai?.price_levels || {};
                    const lo = pl.ideal_buy_range_low;
                    const hi = pl.ideal_buy_range_high;
                    return (
                      <td style={{ ...tdStyle, textAlign: 'center', fontSize: '0.72rem' }}>
                        {lo != null && hi != null ? (
                          <span style={{ color: '#0369a1', fontWeight: 600 }}>
                            ₹{Number(lo).toLocaleString('en-IN')}–{Number(hi).toLocaleString('en-IN')}
                          </span>
                        ) : '—'}
                      </td>
                    );
                  })()}
                  {aiData && (
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {ai ? <Stars n={ai.conviction} /> : '—'}
                    </td>
                  )}
                  {aiData && (
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {ai && (ai.action === 'ACCUMULATE_NOW' || ai.action === 'BUY_ON_DIPS') ? (
                        <button
                          onClick={e => { e.stopPropagation(); onBuyClick?.(sym, ai); }}
                          style={{
                            padding: '0.15rem 0.45rem', fontSize: '0.68rem', fontWeight: 700,
                            background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4,
                            cursor: 'pointer',
                          }}
                        >Add</button>
                      ) : null}
                    </td>
                  )}
                </tr>
                {isExpanded && a && <AnalysisRow data={a} colSpan={activeCols.length} />}
                {isExpanded && ai && <AiInsightRow data={ai} colSpan={activeCols.length} />}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function PortfolioTable({ rows, type = 'holdings', analysisData, aiData, expandedRows, onToggleExpand, onBuyClick }) {
  // When analysis or AI data is loaded for holdings, use expandable custom table
  if (type === 'holdings' && (analysisData || aiData)) {
    return (
      <ExpandableHoldingsTable
        rows={rows}
        analysisData={analysisData}
        aiData={aiData}
        expandedRows={expandedRows || new Set()}
        onToggleExpand={onToggleExpand || (() => {})}
        onBuyClick={onBuyClick}
      />
    );
  }

  // Default: MUI DataGrid (unchanged behavior)
  const columns = type === 'positions' ? positionsColumns : holdingsColumns;

  return (
    <div style={{ width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.instrument_token || row.tradingsymbol || row.symbol}
        initialState={{
          sorting: { sortModel: [{ field: 'pnl', sort: 'desc' }] },
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        density="compact"
        sx={{
          border: '1px solid #e2e8f0',
          borderRadius: 2,
          fontSize: '0.82rem',
          '& .MuiDataGrid-columnHeaders': {
            background: '#f8fafc',
            borderBottom: '2px solid #e2e8f0',
            fontSize: '0.73rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: '#64748b',
          },
          '& .MuiDataGrid-row:hover': { background: '#f8fafc' },
          '& .MuiDataGrid-cell': { borderBottom: '1px solid #f1f5f9' },
        }}
        autoHeight
      />
    </div>
  );
}
